import { WidgetWrapperProps } from './types';
import React, { useRef, useState } from 'react';
import { Icon, Loading } from '../UI';

import { useTranslation } from 'react-i18next';
import { SvgProps } from '../UI/Icon/Icon';
import PlaceholderWidget from './Placeholder/Placeholder';
import { Portfolio } from './Portfolio/Portfolio';
import { WidgetWithPie } from './WidgetWithPie';
import { CompareMyPortfolio } from './CompareMyPortfolio/CompareMyPortfolio';
import AssetPerformanceWidget from './AssetPerformance/AssetPerformance';
import HistoricalIndex from './HistoricalIndex/HistoricalIndex';
import { StrategyProgress } from './StrategyProgress';
import CryptoNewsWidget from './Social/CryptoNews';
import RiskRate from './RiskRate/RiskRate';
import Contribution from './Contribution';
import { NFTWidget } from './NFT';
import { NFTExplorer } from './NFTExplorer';
import EconomicCalendar from './EconomicCalendar';

import { Changes } from './Changes';
import StockExplorer from './StockExplorer';
import CryptoExplorer from './CryptoExplorer';
import { StrategyBacktest } from './StrategyBacktest';
import Tippy from '@tippy.js/react';
import { SocialWidget } from './Social';
import { MyBondPortfolio } from './MyBondPortfolio';
import { StrategiesPerformance } from './StrategiesPerformance/StrategiesPerformance';
import { StrategiesOverlap } from './StrategiesOverlap/StrategiesOverlap';
import { StrategiesComparison } from './StrategiesComparison/StrategiesComparison';
import { StrategiesContribution } from './StrategiesContribution/StrategiesContribution';
import { StrategiesBacktest } from './StrategiesBacktest/StrategiesBacktest';
import { StrategiesAllocation } from './StrategiesAllocation/StrategiesAllocation';

import { ErrorBoundary } from 'react-error-boundary';
import { useThemeContext } from '../../context/Theme';
import { GroupBy } from './WidgetWithPie/WidgetWithPie.model';
import { useSnapshot } from '../../hooks/useSnaphot';
import { logger } from '../../helpers/logger';
import InfoTooltip from '../InfoTooltip/Info-tooltip';
import { Debug } from '../Debug/Debug';
import { WikipediaWidget } from './Social/WikipediaWidget';
import { AssetOptionEnum } from '../../constants/enums';

export const WidgetWrapper = ({
  id,
  selected,
  handleWidgetCloseClick,
  draggableActive,
  snapshotActive,
  closeActive,
  infoActive,
  iconActive,
  widgetIcon,
  componentName,
  lastUpdateDate,
  w = 0,
  h = 0,
  position = 0,
  strategyId,
  loading: parentLoading,
  title,
  searchTerm,
  widgetDescription,
}: WidgetWrapperProps) => {
  const { t } = useTranslation();
  const { theme } = useThemeContext();
  const widgetRef = useRef(null);
  const { snapshot, loading: snapshotLoading } = useSnapshot(widgetRef);

  const [isLoading, setIsLoading] = useState(false);
  const [lastWidgetUpdateAt, setLastWidgetUpdateAt] = useState<
    Date | undefined
  >(lastUpdateDate);

  const [legend, setLegend] = useState<JSX.Element | undefined>();

  const defaultParams = {
    loading: setIsLoading,
    width: w,
    height: h,
    standAlone: false,
    setLegend,
    setLastWidgetUpdateAt,
  };

  const renderWidget = () => {
    switch (componentName) {
      case 'BondPortfolio':
        return strategyId ? (
          <MyBondPortfolio {...defaultParams} />
        ) : (
          <PlaceholderWidget width={w} height={h} />
        );
      case 'StrategyBacktest':
        return strategyId ? (
          <StrategyBacktest
            {...defaultParams}
            strategiesId={[strategyId]}
            key={'AssetPerformanceWithStrategyWidget'}
          />
        ) : (
          <PlaceholderWidget width={w} height={h} />
        );
      case 'CompMyPortfolio':
        return strategyId ? (
          <CompareMyPortfolio
            {...defaultParams}
            strategyId={strategyId}
            key={'CompareMyPortfolio'}
          />
        ) : (
          <PlaceholderWidget width={w} height={h} />
        );
      case 'MyGraph':
        return strategyId ? (
          <AssetPerformanceWidget
            {...defaultParams}
            strategyId={strategyId}
            key={'AssetPerformanceWidget'}
          />
        ) : (
          <PlaceholderWidget width={w} height={h} />
        );
      case 'Wikipedia':
        return <WikipediaWidget {...defaultParams} search={searchTerm} />;
      case 'Reddit':
      case 'News':
      case 'Twitter':
      case 'Youtube':
      case 'TechnicalIndicator':
        return (
          <SocialWidget
            {...defaultParams}
            search={searchTerm}
            socialType={componentName}
          />
        );

      case 'CryptoNews':
        return <CryptoNewsWidget {...defaultParams} />;
      case 'Portfolio':
        return strategyId ? (
          <Portfolio
            {...defaultParams}
            strategyId={strategyId}
            key={'Portfolio'}
            menuOptions={[
              AssetOptionEnum.EDIT_ASSET,
              AssetOptionEnum.COPY_TO,
              AssetOptionEnum.DELETE,
            ]}
          />
        ) : (
          <PlaceholderWidget width={w} height={h} />
        );

      case 'HistoricalIndex':
        return strategyId ? (
          <HistoricalIndex
            {...defaultParams}
            options={{ strategyId: strategyId }}
          />
        ) : (
          <PlaceholderWidget width={w} height={h} />
        );
      case 'StrategyAllocation':
      case 'AssetBySource':
      case 'AssetByInspiration':
      case 'AssetByExchange':
      case 'AssetByCountry':
      case 'AssetByIndustry':
      case 'AssetByCurrency':
        return strategyId ? (
          <WidgetWithPie
            {...defaultParams}
            strategyId={strategyId}
            groupBy={
              componentName === 'StrategyAllocation'
                ? 'STRATEGIES'
                : (componentName
                    .replace('AssetBy', '')
                    .toLocaleUpperCase() as GroupBy)
            }
          />
        ) : (
          <PlaceholderWidget width={w} height={h} />
        );
      case 'PriceTargets':
        return strategyId ? (
          <StrategyProgress
            {...defaultParams}
            options={{ strategyId: strategyId as string }}
          />
        ) : (
          <PlaceholderWidget width={w} height={h} />
        );
      case 'RiskRate':
        return strategyId ? (
          <RiskRate
            {...defaultParams}
            options={{ strategyId: strategyId as string }}
          />
        ) : (
          <PlaceholderWidget width={w} height={h} />
        );
      case 'PriceChanges':
      case 'VolumeChanges':
        return strategyId ? (
          <Changes {...defaultParams} target={componentName} />
        ) : (
          <PlaceholderWidget width={w} height={h} />
        );
      case 'StockExplorer':
        return <StockExplorer {...defaultParams} />;

      // COMPARE PAGE
      case 'StrategiesPerformance':
        return <StrategiesPerformance {...defaultParams} />;
      case 'StrategiesAllocation':
        return <StrategiesAllocation width={w} />;
      case 'StrategiesBacktest':
        return <StrategiesBacktest width={w} />;
      case 'PortfolioOverlap':
        return <StrategiesOverlap {...defaultParams} />;
      case 'StrategiesComparison':
        return <StrategiesComparison {...defaultParams} />;

      case 'StrategiesContribution':
        return <StrategiesContribution width={w} />;
      case 'CryptoExplorer':
        return <CryptoExplorer {...defaultParams} />;
      case 'NFTExplorer':
        return <NFTExplorer {...defaultParams} />;
      case 'Contribution':
        return <Contribution {...defaultParams} strategyId={strategyId} />;
      case 'EconomicCalendar':
        return <EconomicCalendar {...defaultParams} />;
      case 'NFT':
        return <NFTWidget {...defaultParams} />;
      // case 'EsgRating':
      //   return <EsgGrid height={h} width={w} loading={setIsLoading} />;
      default:
        return (
          <PlaceholderWidget
            width={w}
            height={h}
            title={title}
            subtitle={'Coming soon...'}
          />
        );
    }
  };

  return (
    <div
      className={`
        relative
        flex
        flex-col
        h-full
        dark:bg-grey-90
        dark:text-white
        bg-white
        border
        border-grey-30
        dark:border-grey-70
        rounded-xl
        shadow-md
      `}
      ref={widgetRef}
      id={componentName}
    >
      <Debug>
        <>{`${componentName}: W: ${w}: H: ${h} Pos: ${position} - Selected: ${selected} - ID: ${id}`}</>
      </Debug>
      {/* HEADER */}
      <div
        className={`
          flex
          flex-shrink-0
          items-center
          justify-between
          w-full
          border-b
          border-grey-10
          p-3.5
          dark:bg-grey-90
          dark:border-grey-70
          rounded-t-xl
        `}
      >
        <div className="flex items-center">
          {iconActive && (
            <img src={widgetIcon} alt={title} className={'w-2 h-2 mr-2'} />
          )}

          {draggableActive && (
            <span
              className={`
                WidgetsGridDragHandle
                text-md
                text-grey-50
                cursor-pointer
                hidden-from-snapshot
              `}
            >
              <Icon svgName={'drag'} />
            </span>
          )}
          <span className="text-sm font-semibold uppercase ml-1 mr-2">
            {title}
          </span>

          {['Reddit', 'Youtube', 'Twitter', 'Wikipedia'].indexOf(
            componentName
          ) !== -1 && (
            <Icon
              className="mr-2 w-4"
              svgName={componentName.toLowerCase() as SvgProps['svgName']}
            />
          )}

          {infoActive && (
            <Tippy
              content={
                <div className={'text-grey-10 font-medium text-base p-6'}>
                  {widgetDescription}
                </div>
              }
              placement={'bottom'}
            >
              <div>
                <Icon
                  className="cursor-pointer  w-3 hidden-from-snapshot"
                  color="black"
                  svgName={'info'}
                  onClick={() => {}}
                />
              </div>
            </Tippy>
          )}
        </div>
        <div className={'flex hidden-from-snapshot items-center'}>
          {snapshotActive &&
            (snapshotLoading ? (
              <Loading className="mr-3" size="small" status />
            ) : (
              <Icon
                className="cursor-pointer mr-4 w-3 "
                color="black"
                svgName={'camera'}
                onClick={() => {
                  const fileName =
                    'Widget-' +
                    (title || '').replace(/[^a-z0-9]/gi, '') +
                    '-' +
                    new Date().toISOString();
                  snapshot(w, h, fileName, theme, true);
                }}
              />
            ))}
          {closeActive && (
            <Icon
              className="cursor-pointer w-3"
              color="black"
              svgName={'close'}
              onClick={() => {
                handleWidgetCloseClick && handleWidgetCloseClick(id);
              }}
            />
          )}
        </div>
      </div>

      {/* BODY */}
      <div className="flex flex-1 w-full h-full overflow-auto">
        <Loading
          status={isLoading || (parentLoading && selected)}
          size={'large'}
          className={'text-center m-auto'}
        />
        <div
          className={`
            w-full
            h-full
            flex
            flex-col
            ${isLoading || (parentLoading && selected) ? 'hidden' : ''}
          `}
        >
          <ErrorBoundary
            FallbackComponent={() => (
              <div className="flex mt-32 w-full">
                <p className="mx-auto p-20 bg-grey-5">{t('errorBoundary')}</p>
              </div>
            )}
            onError={(error) =>
              logger.error({ componentName, widgetWrapperError: error })
            }
          >
            {renderWidget()}
          </ErrorBoundary>
        </div>
      </div>

      {/* FOOTER */}
      <div
        className={`
          flex
          flex-shrink-0
          h-12
          items-center
          justify-between
          w-full
          border-t
          border-grey-10
          px-3.5
          dark:border-grey-70
        `}
      >
        <div className="flex-none text-sm w-1/3 italic text-grey-50">
          {t('widget.lastUpdate')}
          <span className={'text-xs text-gray-30 ml-1'}>
            {lastWidgetUpdateAt && lastWidgetUpdateAt.toLocaleTimeString()}
          </span>
          <div className={'inline-flex'}>
            <InfoTooltip
              isTooltipIconVisible
              content={t('widget.lastUpdateTooltip')}
              tooltipIconClassName={'ml-2 text-grey-50'}
            />
          </div>
        </div>
        {legend}
      </div>
    </div>
  );
};
